/**
 * Curator Agent + Account Quota endpoints, mirroring the backend DTOs in
 * `agents-service/src/agents/curatorAgent/model`. Every date is epoch milliseconds.
 */

/**
 * Lifecycle state of an agent. Active/Paused are user driven; Standby/Error are system driven -
 * Standby from account-wide quota exhaustion, Error from all of an agent's sources failing.
 */
export enum AgentStatusEnum {
    active = 'ACTIVE',
    paused = 'PAUSED',
    standby = 'STANDBY',
    error = 'ERROR'
}

/**
 * Lifecycle state of a single source. Standby is set by the service, not by the user: an account
 * that passes its quota has its active sources parked until the pool resets.
 */
export enum CurationAgentSourceStatusEnum {
    active = 'ACTIVE',
    paused = 'PAUSED',
    error = 'ERROR',
    standby = 'STANDBY'
}

/** What to do with a mention once the agent has decided on it */
export enum CurationActionTypeEnum {
    saveToAccount = 'SAVE_TO_ACCOUNT',
    saveToFolder = 'SAVE_TO_FOLDER',
    saveToTag = 'SAVE_TO_TAG',
    deleteFromAccount = 'DELETE_FROM_ACCOUNT',
    removeFromFolder = 'REMOVE_FROM_FOLDER',
    removeFromTag = 'REMOVE_FROM_TAG',
    /** Reserved but deferred - the service rejects configuring it until a worker executor exists */
    applySentiment = 'APPLY_SENTIMENT',
    generateInsight = 'GENERATE_INSIGHT',
    sendToRss = 'SEND_TO_RSS'
}

/** Action types whose `actionReferenceIds` is required */
export const actionTypesRequiringReference: CurationActionTypeEnum[] = [
    CurationActionTypeEnum.saveToFolder,
    CurationActionTypeEnum.saveToTag,
    CurationActionTypeEnum.removeFromFolder,
    CurationActionTypeEnum.removeFromTag,
    CurationActionTypeEnum.generateInsight,
    CurationActionTypeEnum.sendToRss
];

/** Action types that act on the mention itself - `actionReferenceIds` must be absent */
export const actionTypesForbiddingReference: CurationActionTypeEnum[] = [
    CurationActionTypeEnum.saveToAccount,
    CurationActionTypeEnum.deleteFromAccount
];

/** No worker executor exists for these yet, so the service refuses to configure them */
export const unsupportedActionTypes: CurationActionTypeEnum[] = [CurationActionTypeEnum.applySentiment];

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * One entry in `positiveActions`/`negativeActions`. Which array it sits in is the trigger, and the
 * array's order is the execution order - there is no `trigger` or `position` field.
 *
 * Omit `id` to create; pass an existing id to update in place. Any existing action for that trigger
 * whose id is absent from the request is deleted.
 */
export interface CurationAgentActionInput {
    id?: number;
    actionType: CurationActionTypeEnum;
    /**
     * Every folder/tag/RSS feed the action targets, or exactly one custom prompt id on
     * GENERATE_INSIGHT's custom path. Up to 50 ids, each at most 255 chars, no duplicates.
     */
    actionReferenceIds?: string[];
    /**
     * GENERATE_INSIGHT only: a prebuilt insight to generate instead of a custom prompt. Exactly one
     * of this and `actionReferenceIds` may be set on that type.
     *
     * Carries the agents-service spelling, which prefixes the platform's own name - see
     * {@link toApiInsightType}.
     */
    insightType?: string;
    /**
     * GENERATE_INSIGHT only, and only on the insight types that read a context: the keywords the
     * insight is written about. Up to {@link maxInsightKeywords}.
     */
    keywords?: string[];
    /** GENERATE_INSIGHT only: the language the insight is written in, eg. en-US. Defaults to en-US */
    languageTag?: string;
}

/** As many keywords as the service accepts on one action */
export const maxInsightKeywords: number = 20;

/**
 * agents-service names its prebuilt insights after the completion formats they come from, so every
 * one of them is the platform's own name behind this prefix. Its InsightType enum is otherwise the
 * same set the panel offers, minus the custom variants - a custom insight travels as a prompt id in
 * `actionReferenceIds` instead.
 */
const insightTypePrefix: string = 'MENTION_INSIGHTS_';

/** A platform mention insight type in the spelling agents-service validates against */
export function toApiInsightType(insightType: string): string {
    return insightType ? `${insightTypePrefix}${insightType}` : null;
}

/** The inverse of {@link toApiInsightType}, tolerating a value that never carried the prefix */
export function fromApiInsightType(insightType: string): string {
    return insightType?.startsWith(insightTypePrefix)
        ? insightType.slice(insightTypePrefix.length)
        : insightType ?? null;
}

export interface CurationAgentActionResponse {
    id: number;
    actionType: CurationActionTypeEnum;
    actionReferenceIds: string[] | null;
    /** Set on GENERATE_INSIGHT when the action runs a prebuilt insight rather than a custom prompt */
    insightType: string | null;
    /** GENERATE_INSIGHT only - see {@link CurationAgentActionInput.keywords} */
    keywords: string[] | null;
    languageTag: string | null;
}

// ---------------------------------------------------------------------------
// Sources - /api/v1/agents/curator/:id/sources
// ---------------------------------------------------------------------------

/** Body for `POST /api/v1/agents/curator/:id/sources`. */
export interface CreateCurationAgentSourceRequest {
    /** topic / folder / tag / ... - max 32 chars */
    sourceType: string;
    /** Id/value within sourceType - max 255 chars */
    sourceRef: string;
    /**
     * A saved mention search holding the filters applied on top of the source. The worker reads it
     * back when it polls, so the filters narrow what the agent picks up.
     */
    filterSearchCriteriaId?: number;
}

/** Body for `PATCH /api/v1/agents/curator/:id/sources/:sourceId`. */
export interface UpdateCurationAgentSourceStatusRequest {
    status: CurationAgentSourceStatusEnum.active | CurationAgentSourceStatusEnum.paused;
}

export interface CurationAgentSourceResponse {
    id: number;
    sourceType: string;
    sourceRef: string;
    /** The saved mention search carrying this source's filters, or null when it has none */
    filterSearchCriteriaId: number | null;
    status: CurationAgentSourceStatusEnum;
    consecutiveFailureCount: number;
    lastScannedAt: number | null;
    createdAt: number;
    updatedAt: number;
}

// ---------------------------------------------------------------------------
// Agents - /api/v1/agents/curator
// ---------------------------------------------------------------------------

/** Body for `POST /api/v1/agents/curator`. */
export interface CreateCurationAgentRequest {
    /** 1-255 chars */
    name: string;
    isFavourite?: boolean;
    isHidden?: boolean;
    displayGroupId?: number;
    /** Id of an existing custom_prompt of a curator type - COVERAGE_CURATION or BRIEFING_CURATOR */
    customPromptId: number;
    positiveActions?: CurationAgentActionInput[];
    negativeActions?: CurationAgentActionInput[];
}

/**
 * Body for `PATCH /api/v1/agents/curator/:id`. An omitted action list is left untouched; an empty
 * one clears it. There is no partial or single-action update.
 */
export interface UpdateCurationAgentRequest {
    name?: string;
    isFavourite?: boolean;
    isHidden?: boolean;
    displayGroupId?: number | null;
    customPromptId?: number;
    positiveActions?: CurationAgentActionInput[];
    negativeActions?: CurationAgentActionInput[];
}

/** The list shape. Sources are included, so a list UI needs no follow up call per agent */
export interface CurationAgentListItemResponse {
    id: number;
    accountId: number;
    name: string;
    status: AgentStatusEnum;
    /** Frontend list toggles */
    isFavourite: boolean;
    isHidden: boolean;
    /** Opaque frontend grouping id for the agent list */
    displayGroupId: number | null;
    createdByUserId: number;
    createdDate: number;
    lastModifiedByUserId: number | null;
    lastModifiedDate: number | null;
    sources: CurationAgentSourceResponse[];
    customPromptId: number;
}

/** The single agent shape (get/create/update), which adds the actions the list omits */
export interface CurationAgentResponse extends CurationAgentListItemResponse {
    /** Run, in order, when a mention matches the agent's criteria */
    positiveActions: CurationAgentActionResponse[];
    /** Run, in order, when a mention does not match */
    negativeActions: CurationAgentActionResponse[];
}

// ---------------------------------------------------------------------------
// Ad hoc curation - POST /api/v1/agents/curator/:id/mentions
// ---------------------------------------------------------------------------

export const maxAdhocMentionRefs: number = 200;

/** How an ad hoc submission was started. Display only - both kinds resolve to a list of mentions */
export enum AgentAdhocSourceKindEnum {
    mentions = 'MENTIONS',
    query = 'QUERY'
}

/** No pending state: the mention count is known when the submission is accepted */
export enum AgentAdhocSourceStatusEnum {
    inProgress = 'IN_PROGRESS',
    complete = 'COMPLETE'
}

/** One ad hoc curation run - `GET /api/v1/agents/curator/:id/adhoc` */
export interface AgentAdhocSourceResponse {
    id: number;
    /** Which agent the run belongs to, which matters when listing across the account */
    agentId: number;
    kind: AgentAdhocSourceKindEnum;
    status: AgentAdhocSourceStatusEnum;
    totalMentions: number;
    /** Mentions accounted for so far whatever the outcome, which is what drives completion */
    evaluatedMentions: number;
    /** Of those, the ones the agent curated. A running count, for display */
    curatedMentions: number;
    /** Epoch ms */
    createdAt: number;
    /** Epoch ms the run completed, or null while it is still working */
    completedAt: number | null;
    createdByUserId: number;
    /**
     * Where the run's mentions came from, in the same terms a live source uses. Only set on a run
     * submitted as a source reference - a mention or query run has no source behind it.
     */
    sourceType: string | null;
    sourceRef: string | null;
    /** The saved mention search carrying that reference's filters, if it had any */
    filterSearchCriteriaId: number | null;
}

/** Exactly one of `mentionRefs`, `query` or `sourceType` + `sourceRef` must be set */
export interface CreateCurationAgentMentionRequest {
    /** Already known mention ids to curate ad hoc, up to {@link maxAdhocMentionRefs} */
    mentionRefs?: string[];
    /** A boolean mention search query patch, resolved into mentions by the polling worker */
    query?: Record<string, unknown>;
    /**
     * A source to run over once, in the same terms a live source uses. Resolved when the run is
     * submitted, over the whole source rather than a date range, and rejected past the ad hoc cap.
     */
    sourceType?: string;
    sourceRef?: string;
    /** A saved mention search holding the filters to narrow that source by */
    filterSearchCriteriaId?: number;
}

// ---------------------------------------------------------------------------
// Notification preferences - /api/v1/agents/curator/notification-preferences
// ---------------------------------------------------------------------------

/** Whether the caller is sent the account's quota threshold warnings. Defaults to true when unset */
export interface NotificationPreferenceResponse {
    enabled: boolean;
}

/** Body for `PUT /api/v1/agents/curator/notification-preferences` */
export interface SetNotificationPreferenceRequest {
    enabled: boolean;
}

// ---------------------------------------------------------------------------
// Quota
// ---------------------------------------------------------------------------

/** Query params for both the self service and admin usage endpoints */
export interface AccountQuotaUsageQuery {
    /** Defaults to 'current' */
    period?: 'current' | 'previous' | 'range';
    /** 1 = the period immediately before the current one. Only used when period=previous */
    offset?: number;
    /** Epoch ms, inclusive. Required when period=range */
    start?: number;
    /** Epoch ms, inclusive. Required when period=range */
    end?: number;
    /** Restrict hours/total to one agent. Omit for account-wide */
    agentId?: string;
    /** A source id, or the literal 'adhoc' (which needs agentId). Omit for every source */
    sourceId?: string;
    /** One ad hoc run's id. Implies sourceId=adhoc, and needs no agentId */
    adhocId?: string;
    /**
     * `source` also fills `hours` on every entry of `sources`. Not allowed with period=previous.
     */
    breakdown?: 'source';
}

export interface HourlyUsage {
    /** Epoch ms, UTC start of this hour */
    hour: number;
    /** Mentions analyzed, and so charged against the quota, this hour */
    count: number;
    /** Of those, the ones the agent curated */
    curatedMentions: number;
}

/** One (agent, source-or-adhoc) bucket's total over the queried range */
export interface SourceUsage {
    agentId: number;
    /** Null for ad hoc submissions that came in without a source */
    sourceId: number | null;
    /** The ad hoc run behind this bucket, set instead of `sourceId` - each run is its own bucket */
    adhocId: number | null;
    count: number;
    /** Of those, the ones the agent curated */
    curatedMentions: number;
    /** This source's own hourly series, returned only when the query asked for a source breakdown */
    hours?: HourlyUsage[];
}

/** `current`/`range` populate `hours`; `previous` populates `total` and `offset` instead */
export interface AccountQuotaUsageResponse {
    period: 'current' | 'previous' | 'range';
    start: number;
    end: number;
    /** One entry per UTC hour in [start, end], zero filled, ready to chart */
    hours?: HourlyUsage[];
    total?: number;
    /** Of `total`, the ones the agent curated. Only returned for period=previous */
    curatedMentions?: number;
    offset?: number;
    /** Every bucket with activity in the range, regardless of the agentId/sourceId filter */
    sources: SourceUsage[];
}

/** `GET /api/v1/agents/curator/quota` - the account's pool, shared across every agent */
export interface CuratorQuotaResponse {
    accountId: number;
    monthlyLimit: number;
    /** Support granted extra for this period only, on top of monthlyLimit. Resets with the period */
    additionalQuota: number;
    /** monthlyLimit + additionalQuota - the limit actually enforced */
    totalLimit: number;
    count: number;
    /** max(0, totalLimit - count) */
    remaining: number;
    windowStart: number;
    nextResetDate: number;
    usage: AccountQuotaUsageResponse;
}

// ---------------------------------------------------------------------------
// Account quota - admin: /api/v1/agents/accounts/:accountId/quota
// ---------------------------------------------------------------------------

/** Body for `PUT /api/v1/agents/accounts/:accountId/quota`. Never resets an existing pool's count */
export interface AccountQuotaConfigRequest {
    /** >= 1 */
    monthlyLimit: number;
    /** Day of month this account resets on, 1-31 */
    billingAnchorDay: number;
}

export interface AccountQuotaResponse {
    accountId: number;
    monthlyLimit: number;
    billingAnchorDay: number;
    /** Support granted extra for this period only, set through its own endpoint */
    additionalQuota: number;
    /** monthlyLimit + additionalQuota - the limit actually enforced */
    totalLimit: number;
    count: number;
    /** max(0, totalLimit - count) */
    remaining: number;
    windowStart: number;
    nextResetDate: number;
    /** Highest threshold (50/75/90/100) already notified this period, or null */
    notifiedThreshold: number | null;
}

/**
 * What an account with no pool configured has: nothing granted and nothing spent. agents-service
 * 404s rather than returning this, so it is built here - see `CurationAgentsApiService.getQuota`.
 */
export function emptyCuratorQuota(): CuratorQuotaResponse {
    return {
        accountId: null,
        monthlyLimit: 0,
        additionalQuota: 0,
        totalLimit: 0,
        count: 0,
        remaining: 0,
        windowStart: null,
        nextResetDate: null,
        usage: {
            period: 'current',
            start: null,
            end: null,
            hours: [],
            sources: []
        }
    };
}

/** Body for `PATCH /api/v1/agents/accounts/:accountId/quota/additional`. An absolute set */
export interface SetAccountQuotaAdditionalRequest {
    additionalQuota: number;
}
